import React, { useState } from 'react';
import type { FamilyToolsMember } from './types';
import AddFamilyMemberDialog from './AddFamilyMemberDialog';
import AddToFamilyDialog from './AddToFamilyDialog';
import GroupIntoFamilyDialog from './GroupIntoFamilyDialog';
import MakePrimaryDialog from './MakePrimaryDialog';
import RemoveFromFamilyDialog from './RemoveFromFamilyDialog';
import { isJuniorForYear, clubYear } from '@shared/membership';

type OpenDialog = 'ADD_PERSON' | 'ADD_TO_FAMILY' | 'GROUP' | 'MAKE_PRIMARY' | 'REMOVE' | null;

interface FamilyActionsProps {
  /** The member currently open in the edit modal. */
  member: FamilyToolsMember;
  /** The whole roster, used for search in Add to a family / Group into a family. */
  allMembers: FamilyToolsMember[];
  /** Called after any action completes successfully, so the caller can refresh and toast. */
  onChanged: (message: string) => void;
}

/**
 * Admin family tools (#127 slice 4, design §7.1): Add family member, Add to
 * a family, Group into a family, Make primary, Remove from family. Rendered
 * inside EditMemberModal in place of the old free "Primary member" checkbox.
 */
const FamilyActions: React.FC<FamilyActionsProps> = ({ member, allMembers, onChanged }) => {
  const [open, setOpen] = useState<OpenDialog>(null);
  const year = clubYear();

  const inFamily = !!member.familyGroupId;
  const isAdult = !isJuniorForYear(member.dateOfBirth, year);
  const familyMembers = inFamily
    ? allMembers.filter((m) => m.familyGroupId === member.familyGroupId && m.isActive !== false)
    : [];
  const primary = familyMembers.find((m) => m.familyPrimary);

  const handleDone = (result: { message: string }) => {
    setOpen(null);
    onChanged(result.message);
  };

  const buttonCls =
    'min-h-[44px] rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-fishing-blue disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Family</p>

      {inFamily ? (
        <p className="text-sm text-gray-900 mb-3">
          Primary: {primary ? `${primary.firstName} ${primary.lastName}` : 'Unknown'}
        </p>
      ) : (
        <p className="text-sm text-gray-500 mb-3">Not part of a family</p>
      )}

      <div className="flex flex-wrap gap-2">
        <button type="button" className={buttonCls} onClick={() => setOpen('ADD_PERSON')}>
          Add family member
        </button>
        {!inFamily && (
          <button type="button" className={buttonCls} onClick={() => setOpen('ADD_TO_FAMILY')}>
            Add to a family
          </button>
        )}
        {!inFamily && (
          <button type="button" className={buttonCls} onClick={() => setOpen('GROUP')}>
            Group into a family
          </button>
        )}
        {inFamily && !member.familyPrimary && isAdult && (
          <button type="button" className={buttonCls} onClick={() => setOpen('MAKE_PRIMARY')}>
            Make primary
          </button>
        )}
        {inFamily && (
          <button
            type="button"
            className={`${buttonCls} border-red-300 text-red-700 hover:bg-red-50`}
            onClick={() => setOpen('REMOVE')}
          >
            Remove from family
          </button>
        )}
      </div>

      {open === 'ADD_PERSON' && (
        <AddFamilyMemberDialog
          familyGroupId={member.familyGroupId || null}
          currentFamily={inFamily ? familyMembers : [member]}
          onClose={() => setOpen(null)}
          onDone={handleDone}
        />
      )}
      {open === 'ADD_TO_FAMILY' && (
        <AddToFamilyDialog
          member={member}
          allMembers={allMembers}
          onClose={() => setOpen(null)}
          onDone={handleDone}
        />
      )}
      {open === 'GROUP' && (
        <GroupIntoFamilyDialog
          startingMember={member}
          allMembers={allMembers}
          onClose={() => setOpen(null)}
          onDone={handleDone}
        />
      )}
      {open === 'MAKE_PRIMARY' && (
        <MakePrimaryDialog member={member} onClose={() => setOpen(null)} onDone={handleDone} />
      )}
      {open === 'REMOVE' && (
        <RemoveFromFamilyDialog
          member={member}
          familyMembers={familyMembers}
          onClose={() => setOpen(null)}
          onDone={handleDone}
        />
      )}
    </div>
  );
};

export default FamilyActions;
